/**
 * 📱 Device Fingerprinting Service
 * Creates unique device identifiers for security and user experience
 * Privacy-conscious implementation - no invasive tracking
 */

import { Platform } from 'react-native';
import { canvasFingerprint } from './canvasFingerprint';

export interface DeviceInfo {
  fingerprint: string;
  deviceType: 'mobile' | 'tablet' | 'desktop';
  browser: BrowserInfo;
  os: OSInfo;
  screen: ScreenInfo;
  timezone: string;
  language: string;
  timestamp: number;
}

export interface BrowserInfo {
  name: string;
  version: string;
  engine: string;
}

export interface OSInfo {
  name: string;
  version: string;
  platform: string;
}

export interface ScreenInfo {
  width: number;
  height: number;
  colorDepth: number;
  pixelRatio: number;
}

export class DeviceFingerprinting {
  private static instance: DeviceFingerprinting;
  private cachedFingerprint: string | null = null;
  private cachedDeviceInfo: DeviceInfo | null = null;

  static getInstance(): DeviceFingerprinting {
    if (!DeviceFingerprinting.instance) {
      DeviceFingerprinting.instance = new DeviceFingerprinting();
    }
    return DeviceFingerprinting.instance;
  }

  /**
   * Generate a privacy-conscious device fingerprint
   * Uses non-invasive browser features only
   */
  generateFingerprint(): string {
    if (this.cachedFingerprint) {
      return this.cachedFingerprint;
    }

    const components = [
      this.getScreenFingerprint(),
      this.getTimezoneFingerprint(),
      this.getLanguageFingerprint(),
      this.getPlatformFingerprint(),
      this.getCanvasFingerprint(),
      this.getBrowserFingerprint()
    ];

    const fingerprint = this.hashComponents(components);
    this.cachedFingerprint = fingerprint;
    
    return fingerprint;
  }

  /**
   * Get comprehensive device information
   */
  getDeviceInfo(): DeviceInfo {
    if (this.cachedDeviceInfo) {
      return this.cachedDeviceInfo;
    }

    const deviceInfo: DeviceInfo = {
      fingerprint: this.generateFingerprint(),
      deviceType: this.getDeviceType(),
      browser: this.getBrowserInfo(),
      os: this.getOSInfo(),
      screen: this.getScreenInfo(),
      timezone: this.getTimezone(),
      language: this.getLanguage(),
      timestamp: Date.now()
    };

    this.cachedDeviceInfo = deviceInfo;
    return deviceInfo;
  }

  /**
   * Check if device fingerprint has changed (potential security concern)
   */
  hasDeviceChanged(previousFingerprint: string): boolean {
    const currentFingerprint = this.generateFingerprint();
    return currentFingerprint !== previousFingerprint;
  }

  /**
   * Get device type based on user agent and screen size
   */
  private getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
    // React Native: Use Platform.OS
    if (Platform.OS !== 'web') {
      return Platform.OS === 'ios' || Platform.OS === 'android' ? 'mobile' : 'desktop';
    }
    
    if (typeof navigator === 'undefined' || typeof screen === 'undefined') {
      return 'desktop'; // Fallback
    }
    
    const ua = navigator.userAgent.toLowerCase();
    const screenWidth = screen.width;

    // Check for mobile patterns
    if (/mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(ua)) {
      return 'mobile';
    }

    // Check for tablet patterns
    if (/tablet|ipad|playbook|silk/i.test(ua) || (screenWidth >= 768 && screenWidth <= 1024)) {
      return 'tablet';
    }

    return 'desktop';
  }

  /**
   * Get detailed browser information
   */
  private getBrowserInfo(): BrowserInfo {
    // React Native: Return native browser info
    if (Platform.OS !== 'web' || typeof navigator === 'undefined') {
      return { name: Platform.OS === 'ios' ? 'Safari' : 'Chrome', version: 'native', engine: 'Native' };
    }
    
    const ua = navigator.userAgent;
    
    // Chrome
    if (ua.includes('Chrome') && !ua.includes('Edg')) {
      const version = ua.match(/Chrome\/(\d+)/)?.[1] || 'unknown';
      return { name: 'Chrome', version, engine: 'Blink' };
    }
    
    // Firefox
    if (ua.includes('Firefox')) {
      const version = ua.match(/Firefox\/(\d+)/)?.[1] || 'unknown';
      return { name: 'Firefox', version, engine: 'Gecko' };
    }
    
    // Safari
    if (ua.includes('Safari') && !ua.includes('Chrome')) {
      const version = ua.match(/Version\/(\d+)/)?.[1] || 'unknown';
      return { name: 'Safari', version, engine: 'WebKit' };
    }
    
    // Edge
    if (ua.includes('Edg')) {
      const version = ua.match(/Edg\/(\d+)/)?.[1] || 'unknown';
      return { name: 'Edge', version, engine: 'Blink' };
    }

    return { name: 'Unknown', version: 'unknown', engine: 'unknown' };
  }

  /**
   * Get operating system information
   */
  private getOSInfo(): OSInfo {
    // React Native: Use Platform.OS
    if (Platform.OS !== 'web') {
      return { name: Platform.OS === 'ios' ? 'iOS' : 'Android', version: 'native', platform: Platform.OS };
    }
    
    if (typeof navigator === 'undefined') {
      return { name: 'Unknown', version: 'unknown', platform: 'unknown' };
    }
    
    const ua = navigator.userAgent;
    const platform = navigator.platform;

    // Windows
    if (ua.includes('Windows')) {
      const version = ua.match(/Windows NT (\d+\.\d+)/)?.[1] || 'unknown';
      return { name: 'Windows', version, platform };
    }

    // macOS
    if (ua.includes('Mac OS X')) {
      const version = ua.match(/Mac OS X (\d+[._]\d+[._]\d+)/)?.[1]?.replace(/_/g, '.') || 'unknown';
      return { name: 'macOS', version, platform };
    }

    // iOS
    if (ua.includes('iPhone') || ua.includes('iPad')) {
      const version = ua.match(/OS (\d+[._]\d+[._]\d+)/)?.[1]?.replace(/_/g, '.') || 'unknown';
      return { name: 'iOS', version, platform };
    }

    // Android
    if (ua.includes('Android')) {
      const version = ua.match(/Android (\d+\.\d+)/)?.[1] || 'unknown';
      return { name: 'Android', version, platform };
    }

    // Linux
    if (ua.includes('Linux')) {
      return { name: 'Linux', version: 'unknown', platform };
    }

    return { name: 'Unknown', version: 'unknown', platform };
  }

  /**
   * Get screen information
   */
  private getScreenInfo(): ScreenInfo {
    // React Native: Use Dimensions API
    if (Platform.OS !== 'web') {
      const { Dimensions } = require('react-native');
      const { width, height } = Dimensions.get('window');
      return {
        width,
        height,
        colorDepth: 24, // Default for mobile
        pixelRatio: Dimensions.get('window').scale || 1
      };
    }
    
    if (typeof screen === 'undefined' || typeof window === 'undefined') {
      return { width: 0, height: 0, colorDepth: 24, pixelRatio: 1 };
    }
    
    return {
      width: screen.width,
      height: screen.height,
      colorDepth: screen.colorDepth,
      pixelRatio: window.devicePixelRatio || 1
    };
  }

  /**
   * Get timezone
   * Uses platform-specific detection: expo-localization on native, Intl on web
   */
  private getTimezone(): string {
    // React Native: use expo-localization or NativeModules (Intl returns 'UTC' on Hermes)
    if (Platform.OS !== 'web') {
      try {
        // expo-localization getCalendars() is the most reliable source on iOS/Android
        const { getCalendars } = require('expo-localization');
        const calendars = getCalendars();
        if (calendars?.[0]?.timeZone) {
          return calendars[0].timeZone;
        }
      } catch {}
      try {
        const { NativeModules } = require('react-native');
        const tz = NativeModules.RNLocalize?.timeZone || NativeModules.I18nManager?.timeZone;
        if (tz) return tz;
      } catch {}
    }

    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return 'unknown';
    }
  }

  /**
   * Get language
   */
  private getLanguage(): string {
    // React Native: Use NativeModules or fallback
    if (Platform.OS !== 'web') {
      try {
        const { NativeModules } = require('react-native');
        return NativeModules.I18nManager?.localeIdentifier || 'en';
      } catch {
        return 'en';
      }
    }
    
    if (typeof navigator === 'undefined' || !navigator.language) {
      return 'en';
    }
    
    return navigator.language;
  }

  /**
   * Generate screen-based fingerprint component
   */
  private getScreenFingerprint(): string {
    const screen = this.getScreenInfo();
    return `${screen.width}x${screen.height}x${screen.colorDepth}x${screen.pixelRatio}`;
  }

  /**
   * Generate timezone fingerprint component
   */
  private getTimezoneFingerprint(): string {
    return this.getTimezone();
  }

  /**
   * Generate language fingerprint component
   */
  private getLanguageFingerprint(): string {
    return this.getLanguage();
  }

  /**
   * Generate platform fingerprint component
   */
  private getPlatformFingerprint(): string {
    // React Native: Use Platform.OS
    if (Platform.OS !== 'web') {
      return Platform.OS;
    }
    
    if (typeof navigator === 'undefined' || !navigator.platform) {
      return 'unknown';
    }
    
    return navigator.platform;
  }

  private getCanvasFingerprint(): string {
    return canvasFingerprint((data) => this.simpleHash(data));
  }

  /**
   * Generate browser fingerprint component
   */
  private getBrowserFingerprint(): string {
    const browser = this.getBrowserInfo();
    return `${browser.name}-${browser.version}-${browser.engine}`;
  }

  /**
   * Hash fingerprint components
   */
  private hashComponents(components: string[]): string {
    const combined = components.join('|');
    return this.simpleHash(combined);
  }

  /**
   * Simple hash function (not cryptographic, just for fingerprinting)
   */
  private simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36).substring(0, 12);
  }

  /**
   * Clear cached fingerprint (force regeneration)
   */
  clearCache(): void {
    this.cachedFingerprint = null;
    this.cachedDeviceInfo = null;
  }

  /**
   * Get device trust score based on consistency
   */
  calculateTrustScore(previousDeviceInfo?: DeviceInfo): number {
    if (!previousDeviceInfo) return 0.5; // Neutral for new devices

    const current = this.getDeviceInfo();
    let score = 1.0;

    // Check fingerprint consistency
    if (current.fingerprint !== previousDeviceInfo.fingerprint) {
      score -= 0.3;
    }

    // Check device type consistency
    if (current.deviceType !== previousDeviceInfo.deviceType) {
      score -= 0.2;
    }

    // Check browser consistency
    if (current.browser.name !== previousDeviceInfo.browser.name) {
      score -= 0.2;
    }

    // Check OS consistency
    if (current.os.name !== previousDeviceInfo.os.name) {
      score -= 0.2;
    }

    // Check timezone consistency (less critical)
    if (current.timezone !== previousDeviceInfo.timezone) {
      score -= 0.1;
    }

    return Math.max(0, Math.min(1, score));
  }

  /**
   * Check if device appears to be a bot or automated system
   */
  isSuspiciousDevice(): boolean {
    // React Native: Not a bot
    if (Platform.OS !== 'web') {
      return false;
    }
    
    if (typeof navigator === 'undefined') {
      return false;
    }
    
    const ua = navigator.userAgent.toLowerCase();
    
    // Check for common bot patterns
    const botPatterns = [
      'bot', 'crawler', 'spider', 'scraper', 'headless',
      'phantom', 'selenium', 'webdriver', 'automation'
    ];

    if (botPatterns.some(pattern => ua.includes(pattern))) {
      return true;
    }

    // Check for suspicious screen dimensions
    const screen = this.getScreenInfo();
    if (screen.width === 0 || screen.height === 0) {
      return true;
    }

    // Check for missing standard browser features
    if (typeof navigator.language === 'undefined' || 
        typeof navigator.platform === 'undefined') {
      return true;
    }

    return false;
  }
}

// Export singleton instance
export const deviceFingerprinting = DeviceFingerprinting.getInstance();
